import { Body, Circle } from "p2";

import { collisionGroups } from "../../config/CollisionGroups";
import { Colors } from "../../config/DesignConstants";
import { createRegularPolygon } from "../../core/util/PolygonUtils";
import { V, V2d } from "../../core/Vector";
import BrokenExpandEffect from "../effects/BrokenExpandEffect";
import VectorSprite from "../graphics/VectorSprite";
import { createPolygonSegments } from "../graphics/VectorSpriteUtils";
import {
  BaseMunition,
  ExplosionConfig,
  MunitionMovement,
  MunitionType,
  MunitionVisualConfig,
} from "./BaseMunition";
import { TrackingMovement } from "./movements/TrackingMovement";

// Missile physics constants
const MISSILE_RADIUS = 3;
const MISSILE_MASS = 0.3;
const MISSILE_SPEED = 60;
const MISSILE_TURN_SPEED = 2.0;
const MISSILE_LIFETIME = 10.0;

// Missile rendering constants
const MISSILE_STROKE_WIDTH = 1;
const MISSILE_FLASH_RATE = 0.1; // seconds
const MISSILE_COLOR_1 = Colors.CYAN;
const MISSILE_COLOR_2 = Colors.PINK;

// Missile trail constants
const TRAIL_INTERVAL = 0.2; // seconds
const TRAIL_LIFETIME = 0.3; // seconds
const TRAIL_VELOCITY_SCALE = 10;
const TRAIL_SIZE = 2;
const TRAIL_HEXAGON_SIDES = 6;

// Missile explosion constants
const MISSILE_EXPLOSION_RADIUS = 20;
const MISSILE_EXPLOSION_LIFETIME = 1.0;
const MISSILE_DAMAGE = 1;

export class Missile extends BaseMunition {
  munitionType = MunitionType.MISSILE;
  damage = MISSILE_DAMAGE;

  private renderer!: VectorSprite;
  private trackingMovement!: TrackingMovement;
  private flashTimer: number = 0;
  private currentColorIndex: number = 0;
  private trailTimer: number = 0;

  constructor(position: V2d) {
    super(position);
    this.tags = ["missile", "levelEntity"];
  }

  protected createBody(position: V2d): Body {
    const body = new Body({ mass: MISSILE_MASS });
    const shape = new Circle({ radius: MISSILE_RADIUS });
    shape.collisionGroup = collisionGroups("Bomb");
    shape.collisionMask = collisionGroups("Turret", "Bullet", "Explosion");
    body.addShape(shape);
    body.position = [position.x, position.y];

    // Start with minimal velocity - tracking will take over
    body.velocity = [0, 0];

    return body;
  }

  protected setupVisual(): void {
    const visualConfig = this.getVisualConfig();
    this.renderer = VectorSprite.fromPolygon(
      visualConfig.points,
      visualConfig.strokeColor,
      visualConfig.strokeWidth || 1,
    );
    this.addChild(this.renderer);
  }

  getMovementBehavior(): MunitionMovement {
    this.trackingMovement = new TrackingMovement(
      MISSILE_SPEED,
      MISSILE_TURN_SPEED,
      MISSILE_LIFETIME,
    );
    return this.trackingMovement;
  }

  getVisualConfig(): MunitionVisualConfig {
    // Create an arrow/triangle shape pointing up (missiles are directional)
    const points = [
      V(0, -MISSILE_RADIUS), // Top point
      V(MISSILE_RADIUS, MISSILE_RADIUS), // Bottom right
      V(-MISSILE_RADIUS, MISSILE_RADIUS), // Bottom left
    ];

    return {
      points,
      strokeColor: MISSILE_COLOR_1, // Start with first color
      strokeWidth: MISSILE_STROKE_WIDTH,
    };
  }

  getExplosionConfig(): ExplosionConfig {
    return {
      radius: MISSILE_EXPLOSION_RADIUS,
      lifetime: MISSILE_EXPLOSION_LIFETIME,
      color: this.currentColorIndex === 0 ? MISSILE_COLOR_1 : MISSILE_COLOR_2,
      damage: this.damage,
    };
  }

  onTick(dt: number) {
    super.onTick(dt);

    if (!this.body || this.exploded) return;

    // Handle color flashing
    this.flashTimer += dt;
    if (this.flashTimer >= MISSILE_FLASH_RATE) {
      this.flashTimer = 0;
      this.currentColorIndex = (this.currentColorIndex + 1) % 2;
      const newColor =
        this.currentColorIndex === 0 ? MISSILE_COLOR_1 : MISSILE_COLOR_2;

      if (this.renderer?.sprite) {
        // Update renderer segments with new color
        const visualConfig = this.getVisualConfig();
        const newSegments = createPolygonSegments(
          visualConfig.points,
          newColor,
          visualConfig.strokeWidth,
        );
        this.renderer.setSegments(newSegments);
      }
    }

    // Create trail particles
    this.trailTimer += dt;
    if (this.trailTimer >= TRAIL_INTERVAL) {
      this.trailTimer = 0;
      this.createTrailEffect();
    }
  }

  private createTrailEffect(): void {
    if (!this.body) return;

    // Get current velocity for trail inheritance
    const missileVelocity = V(this.body.velocity).imul(-0.2); // Trail moves opposite direction

    this.getCurrentLevel()?.addChild(
      new BrokenExpandEffect({
        position: V(this.body.position),
        polygon: createRegularPolygon(TRAIL_HEXAGON_SIDES, TRAIL_SIZE),
        colors: [Colors.ORANGE, Colors.RED],
        lifetime: TRAIL_LIFETIME,
        velocityScale: TRAIL_VELOCITY_SCALE,
        inheritedVelocity: missileVelocity,
      }),
    );
  }

  protected updateVisualPosition(): void {
    if (this.body && this.renderer) {
      this.renderer.setPosition(this.body.position);

      // Rotate missile to face movement direction
      const velocity = V(this.body.velocity);
      if (velocity.magnitude > 0.1) {
        const angle = velocity.angle + Math.PI / 2; // Add 90° since triangle points up
        this.renderer.setRotation(angle);
      }
    }
  }

  protected getExplosionType(): "missile" {
    return "missile";
  }
}
